import { ArrowRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { CrmV3BannerState } from '@/lib/api/crm-v3';

type Props = {
  state: Exclude<CrmV3BannerState, 'hidden'>;
  href: string;
  onOpen: () => void;
  onDismiss: () => void;
};

/* CRM V3 brand scale (apps/crm-v3/src/ui/tokens/theme.css, --color-brand-*): V2's --primary is slate. */
const SURFACE =
  'border-[color-mix(in_srgb,#0aa85c_24%,var(--border))] bg-[linear-gradient(100deg,color-mix(in_srgb,#0aa85c_9%,var(--card))_0%,var(--card)_58%)] dark:border-[color-mix(in_srgb,#3fd18c_22%,var(--border))] dark:bg-[linear-gradient(100deg,color-mix(in_srgb,#0aa85c_16%,var(--card))_0%,var(--card)_62%)]';
const GLOW = 'bg-[radial-gradient(closest-side,color-mix(in_srgb,#3fd18c_32%,transparent),transparent)]';
const MARK =
  'bg-[linear-gradient(140deg,#3fd18c_0%,#0aa85c_48%,#0a6239_100%)] shadow-[inset_0_1px_0_rgb(255_255_255/0.35),0_1px_2px_rgb(5_41_26/0.25)]';
const CTA =
  'bg-[#0aa85c] text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.22),0_1px_2px_rgb(5_41_26/0.2)] hover:bg-[#0a9a54] focus-visible:ring-[#0aa85c]/40 dark:bg-[#0aa85c] dark:hover:bg-[#3fd18c] dark:hover:text-[#05291a]';

/** Invitation to CRM V3 above V2 pages: V3 mark, one line, one link, a discreet close. */
export function CrmV3WaitlistBannerView({ state, href, onOpen, onDismiss }: Props) {
  const { t } = useTranslation('common');

  return (
    <div className="px-4 pt-3 lg:px-6">
      <section
        aria-label={t('crmV3Banner.label')}
        className={`relative flex items-center gap-3 overflow-hidden rounded-xl border py-2.5 pl-3 pr-2 shadow-[0_1px_2px_rgb(15_23_42/0.04)] ${SURFACE}`}
      >
        <span aria-hidden className={`pointer-events-none absolute -left-10 -top-16 size-44 ${GLOW}`} />
        <span
          aria-hidden
          className={`relative grid size-8 shrink-0 place-items-center rounded-lg text-[11px] font-bold tracking-tight text-white ${MARK}`}
        >
          V3
        </span>
        <div className="relative min-w-0 flex-1">
          <p className="line-clamp-3 text-[13px] font-semibold leading-snug text-foreground sm:truncate sm:text-sm">{t(`crmV3Banner.${state}.title`)}</p>
          <p className="hidden truncate text-xs text-muted-foreground sm:block">{t(`crmV3Banner.${state}.body`)}</p>
        </div>
        <a
          href={href}
          onClick={onOpen}
          className={`relative inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold outline-none transition-colors focus-visible:ring-[3px] ${CTA}`}
        >
          {t(`crmV3Banner.${state}.cta`)}
          <ArrowRight className="hidden size-3.5 sm:block" aria-hidden />
        </a>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('crmV3Banner.dismiss')}
          className="relative grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </section>
    </div>
  );
}
